/**
 * Transmittals — list view for AISC §1.7.0 Phase 3 (Form F-1.7.0).
 */

import React, { useEffect, useState } from "react";
import PageGuide from "../../components/PageGuide";
import { prettyStatus } from "../../components/PaperForm/styles";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import {
  Container, Typography, Stack, Button, Paper, TableHead, TableBody,
  TableRow, TableCell, Chip, MenuItem, TextField, Alert, Breadcrumbs,
  Link as MuiLink, Box,
} from "@mui/material";
import { HomeRounded, AddRounded, ForwardToInboxOutlined } from "@mui/icons-material";

import { listTransmittals } from "../../utils/transmittalsApi";
import EmptyState from "../../components/EmptyState/EmptyState";
import { fmtDateISO } from "../../utils/dateFmt";

import SmartTable from "../../components/SmartTable/SmartTable";
const STATUS_COLOR = {
  DRAFT:     { bg: "#e9ecef", color: "#6c757d" },
  SENT:      { bg: "#cfe2ff", color: "#052c65" },
  RETURNED:  { bg: "#fff3cd", color: "#664d03" },
  CLOSED:    { bg: "#198754", color: "#ffffff" },
  CANCELLED: { bg: "#f8d7da", color: "#58151c" },
};

export default function Transmittals() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [statusFilter, setStatusFilter] = useState("");
  const [jobIdFilter, setJobIdFilter] = useState("");

  const refresh = () => {
    setLoading(true); setError(null);
    listTransmittals({
      status: statusFilter || undefined,
      job_id: jobIdFilter || undefined,
    })
      .then(setRows)
      .catch((err) => setError(err?.response?.data?.error || err?.message))
      .finally(() => setLoading(false));
  };
  useEffect(refresh, [statusFilter, jobIdFilter]);

  return (
    <>
      <Helmet><title>Transmittals | SFM</title></Helmet>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <MuiLink href="/" color="inherit" underline="hover">
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <HomeRounded fontSize="small" /> <span>Home</span>
            </Stack>
          </MuiLink>
          <Typography color="text.primary">Transmittals</Typography>
        </Breadcrumbs>

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>Transmittals (Form F-1.7.0)</Typography>
            <Typography variant="caption" sx={{ color: "#6c757d" }}>
              AISC §1.7.0 Phase 3 — outgoing cover sheet for drawings, letters, prints, change orders.
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddRounded />}
            onClick={() => navigate("/transmittals/new")}>
            New Transmittal
          </Button>
        </Stack>
        <PageGuide
          why="Prove what you sent, to whom, and when — a clean paper trail for every drawing or document that leaves the shop."
          who="Whoever sends drawings out; the System keeping the paper trail."
          what="An outgoing cover sheet for drawings, prints, letters, and change orders."
          how="Create a transmittal, list the items and recipient, and send it. It's saved as a record."
          when="Every time you send controlled documents to the customer, engineer, or GC."
          standard="AISC §1.7.0"
        />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" spacing={2}>
            <TextField select label="Status" value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 180 }}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="DRAFT">Draft</MenuItem>
              <MenuItem value="SENT">Sent</MenuItem>
              <MenuItem value="RETURNED">Returned</MenuItem>
              <MenuItem value="CLOSED">Closed</MenuItem>
              <MenuItem value="CANCELLED">Cancelled</MenuItem>
            </TextField>
            <TextField label="Job ID" type="number" value={jobIdFilter}
              onChange={(e) => setJobIdFilter(e.target.value)} sx={{ minWidth: 140 }} />
          </Stack>
        </Paper>

        <Paper>
          <SmartTable tableId="transmittals" lockedColumns={["Transmittal #", "Status"]} size="small">
            <TableHead>
              <TableRow>
                <TableCell>Transmittal #</TableCell>
                <TableCell>Job</TableCell>
                <TableCell>Recipient</TableCell>
                <TableCell>Sent via</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Sent</TableCell>
                <TableCell>Returned</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={7}>Loading…</TableCell></TableRow>}
              {!loading && !rows.length && (
                <TableRow><TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
                  <EmptyState
                    icon={ForwardToInboxOutlined}
                    title="No transmittals yet"
                    description="Click New Transmittal to issue a Form F-1.7.0 cover sheet for drawings sent to a customer or EOR. Each transmittal tracks the lifecycle from DRAFT through SENT, RETURNED, and CLOSED."
                    variant="plain"
                  />
                </TableCell></TableRow>
              )}
              {rows.map((tr) => {
                const c = STATUS_COLOR[tr.status] || { bg: "#e9ecef", color: "#495057" };
                return (
                  <TableRow key={tr.id} hover sx={{ cursor: "pointer" }}
                    onClick={() => navigate(`/transmittals/${tr.id}`)}>
                    <TableCell sx={{ fontFamily: "monospace", fontWeight: 600 }}>{tr.transmittal_number}</TableCell>
                    <TableCell>#{tr.job_id}</TableCell>
                    <TableCell>{tr.recipient_party}</TableCell>
                    <TableCell><Typography variant="caption">{tr.sent_via}</Typography></TableCell>
                    <TableCell>
                      <Chip size="small" label={prettyStatus(tr.status)}
                        sx={{ backgroundColor: c.bg, color: c.color, fontWeight: 600 }} />
                    </TableCell>
                    <TableCell>{fmtDateISO(tr.sent_at)}</TableCell>
                    <TableCell>{fmtDateISO(tr.returned_at)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </SmartTable>
        </Paper>
      </Container>
    </>
  );
}
